import { ToastAction } from "@/components/ui/toast";
import type { TagReference } from "@/domains/documents/components/molecules/DocumentTags";
import {
  addCategoryToDocument,
  removeCategoryFromDocument,
} from "@/domains/documents/utils/categoryHelpers";
import { documentTagReferences } from "@/domains/documents/utils/documentTagReferences";
import {
  addDisciplineToDocument,
  addStageToDocument,
  removeDisciplineFromDocument,
  removeStageFromDocument,
} from "@/domains/documents/utils/tagHelpers";
import { useCallback, useEffect } from "react";
import { reportDocumentTableFailure } from "./documentTableFailure";
import type { DocumentWithTags } from "./documentTableTypes";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableBasics } from "./useTableBasics";
import type { useTablePreviewState } from "./useTablePreviewState";
type Options = Pick<ReturnType<typeof documentTableOptions>, "store" | "buildingId" | "documents"> &
  Pick<ReturnType<typeof useTablePreviewState>, "toast" | "authStore"> &
  Pick<ReturnType<typeof useTableBasics>, "language">;
export function useTableTagActions(options: Options) {
  const { store, buildingId, toast, authStore, documents, language } = options;
  // Handler for removing a tag from a document
  const handleTagRemove = useCallback(
    async (docId: string, tag: TagReference) => {
      const numericDocId = Number(docId);
      if (isNaN(numericDocId)) return;

      // Parse the numeric DB ID from the tag reference ID (e.g., "category-123" → 123)
      const parts = tag.id.split("-");
      const numericTagId = Number(parts[parts.length - 1]);
      if (isNaN(numericTagId)) return;

      try {
        if (tag.type === "category") {
          await removeCategoryFromDocument(numericDocId, tag.name);
        } else if (tag.type === "construction") {
          await removeDisciplineFromDocument(numericDocId, numericTagId);
        } else if (tag.type === "stage") {
          await removeStageFromDocument(numericDocId, numericTagId);
        }

        const refreshDoc = () => {
          if (store) {
            setTimeout(() => store.refreshSingleDocument(numericDocId), 300);
            if (buildingId) {
              setTimeout(() => store.loadAvailableFilters(buildingId), 500);
            }
          }
        };

        refreshDoc();

        toast({
          description: `Tag "${tag.name}" removed`,
          action: (
            <ToastAction
              altText="Undo tag removal"
              onClick={async () => {
                try {
                  const userId = authStore.userProfileId;
                  if (!userId) throw new Error("Not authenticated");
                  if (tag.type === "category") {
                    await addCategoryToDocument(numericDocId, tag.name, userId);
                  } else if (tag.type === "construction") {
                    await addDisciplineToDocument(numericDocId, numericTagId, userId);
                  } else if (tag.type === "stage") {
                    await addStageToDocument(numericDocId, numericTagId, userId);
                  }
                  refreshDoc();
                  toast({ description: `Tag "${tag.name}" restored` });
                } catch {
                  toast({
                    variant: "destructive",
                    description: `Failed to restore tag "${tag.name}"`,
                  });
                }
              }}
            >
              Undo
            </ToastAction>
          ),
        });
      } catch (error) {
        reportDocumentTableFailure("tag_remove", error);
        toast({
          variant: "destructive",
          description: `Failed to remove tag "${tag.name}"`,
        });
      }
    },
    [store, buildingId, toast, authStore]
  );

  // Load documents on mount and when buildingId changes
  // Skip loading if:
  // - store is not provided (public view with override handlers)
  // - buildingId is empty (e.g., trash view)
  // - documents are provided via props (parent already loaded them)
  useEffect(() => {
    if (store && buildingId && buildingId.trim() && documents === undefined) {
      store.loadDocuments(buildingId);
    }
  }, [buildingId, store, documents]);

  // Load tags on mount
  // Skip loading if store not provided, buildingId is empty, or documents provided via props
  useEffect(() => {
    if (store && buildingId && buildingId.trim() && documents === undefined) {
      store.loadTags(buildingId).catch((error) => {
        reportDocumentTableFailure("tags_load", error);
      });
    }
  }, [buildingId, store, documents]);

  // Resolve tags for a document. Prefer the store's copy, which has every
  // relation loaded (filtered rows may not). Store-less callers — the Brief
  // page — fetch complete rows themselves, so their own relations are the tags.
  const resolveTags = (doc: DocumentWithTags): TagReference[] =>
    documentTagReferences(store?.documents.get(doc.id) ?? doc, language);
  return { resolveTags, handleTagRemove };
}
